// Programmatic opener for the group new-joiner welcome dialog.
//
// Owns the two things the dialog component deliberately does not: the suppression rule (should this
// greeting exist at all?) and the queueing behind the platform's own first-run overlay (so a
// brand-new user never gets two stacked modals). Modeled on onboarding/openWelcomeOverlay.tsx.
//
// Design: designs/01-now/2026-07-21-group-new-joiner-welcome-technical-design.md §4-§5.

import React from "react";
import { openSimpleDialog } from "@/main/react/Dialog/SimpleDialog";
import {
    GroupWelcomeDialog,
    type GroupWelcomeContent,
    type GroupWelcomeDialogMode,
} from "@/main/react/group/GroupWelcomeDialog";
import { onboardingState } from "@/main/onboarding/onboardingState";
import type { Localizer } from "@/main/react/Types";
import logger from "@/lib/logger";

const QUEUE_POLL_INTERVAL_MS = 300;
// Ceiling on how long we wait for the platform overlay to clear. If a user leaves the first-run
// carousel open, the greeting is simply dropped rather than ambushing them much later.
const QUEUE_MAX_WAIT_MS = 20_000;

/**
 * The suppression rule (UX §6): a welcome dialog is only worth interrupting for when a human wrote
 * something. Description and topics count toward the dialog's CONTENT but not toward this decision —
 * nearly every group has both, so including them would make the rule vacuous and train people to
 * dismiss the dialog reflexively before ever meeting a group that filled it in properly.
 *
 * Exported for direct testing: this predicate fails silently in both directions (a dialog nobody
 * wanted, or a greeting that never fires), so it is the part most worth pinning down.
 */
export function hasOwnerAuthoredWelcomeContent(content: GroupWelcomeContent): boolean {
    const hasWelcomeMessage = Boolean(content.welcomeMessage?.trim());
    const hasRules = (content.rules ?? []).some((rule) => rule.title?.trim());
    const hasResources = (content.resources ?? []).some(
        (resource) => resource.title?.trim() && resource.url?.trim()
    );
    return hasWelcomeMessage || hasRules || hasResources;
}

/**
 * True while something else is already owning the screen: the platform's first-run welcome carousel
 * (which fires from the global bootstrap and may still be pending), or any other open dialog.
 *
 * The `[role="dialog"]` probe is the reliable way to see an open modal here — `openSimpleDialog`
 * mounts each dialog into its own detached React root with no shared registry, so there is no modal
 * stack to consult.
 */
function isAnotherModalOwningTheScreen(): boolean {
    if (onboardingState.isNewUser()) {
        return true;
    }
    if (typeof document === "undefined") {
        return false;
    }
    return document.querySelector('[role="dialog"]') !== null;
}

function openNow(
    content: GroupWelcomeContent,
    mode: GroupWelcomeDialogMode,
    localizer: Localizer
): () => void {
    return openSimpleDialog(
        ({ open, onOpenChange }) => (
            <GroupWelcomeDialog {...content} open={open} onOpenChange={onOpenChange} mode={mode} localizer={localizer} />
        ),
        { "data-group-welcome-dialog": "1" }
    );
}

/**
 * Opens the welcome dialog in "about" mode. Always opens — this entry point is user-initiated and
 * carries no suppression, so a group with nothing authored still gets its description and topics.
 */
export function openGroupAboutDialog(content: GroupWelcomeContent, localizer: Localizer): () => void {
    return openNow(content, "about", localizer);
}

/** Opens the dialog in "join" mode unconditionally — the owner's "Preview welcome" action. */
export function openGroupWelcomePreview(content: GroupWelcomeContent, localizer: Localizer): () => void {
    return openNow(content, "join", localizer);
}

/**
 * The join/approval trigger. No-ops when the owner authored nothing (UX §6), and waits for the
 * platform's first-run overlay to clear before opening so a brand-new user's first group join never
 * produces two stacked modals.
 *
 * Never throws into the join path: a failed greeting must not make a successful join look broken.
 */
export function maybeOpenGroupWelcomeDialog(content: GroupWelcomeContent, localizer: Localizer): void {
    try {
        if (!hasOwnerAuthoredWelcomeContent(content)) {
            return;
        }

        if (!isAnotherModalOwningTheScreen()) {
            openNow(content, "join", localizer);
            return;
        }

        const startedAt = Date.now();
        const timer = window.setInterval(() => {
            if (!isAnotherModalOwningTheScreen()) {
                window.clearInterval(timer);
                openNow(content, "join", localizer);
                return;
            }
            if (Date.now() - startedAt >= QUEUE_MAX_WAIT_MS) {
                window.clearInterval(timer);
            }
        }, QUEUE_POLL_INTERVAL_MS);
    } catch (error) {
        logger.warn("[group] failed to open the new-joiner welcome dialog", error);
    }
}
